import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import { IndicatorDetails, IndicatorDetailSectionProps } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown } from '@fortawesome/pro-light-svg-icons';
import { getMadLibForIndicator } from '../../ImpactIndicator/IndicatorMadLibs';

const StyledDiv = styled.div`
  .program-heading {
    max-width: 630px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
  }
  .yes-no-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    .no-button,
    .yes-button {
      width: 196px;
      height: 125px;
      display: flex;
      cursor: pointer;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      border-radius: 4px;
      font-size: 16px;
      line-height: 21px;
      letter-spacing: -0.02em;
      border: 1px solid ${COLORS.ctaPrimary};
      transition: background 0.5s;
      background-color: ${COLORS.whiteBase};
      &:hover {
        background-color: ${COLORS.highlightBackground};
      }
      &.selected {
        color: ${COLORS.whiteBase};
        background-color: ${COLORS.ctaPrimary};
        .icon {
          color: ${COLORS.whiteBase};
        }
      }
      .icon {
        color: ${COLORS.ctaPrimary};
        margin-bottom: ${SPACERS.tiny};
      }
    }
    .yes-button {
      margin-right: ${SPACERS.tiny};
    }
    .no-button {
      margin-left: ${SPACERS.tiny};
    }
  }
`;

const IndicatorProgramEnd: React.FC<IndicatorDetailSectionProps & {
  indicatorDetails: IndicatorDetails;
}> = ({
  beneficiaryTerm,
  indicator,
  requestState,
  indicatorDetails,
  selectedIndex,
  selectedSectionKey,
  setIndicators,
  onNext,
  onPrevious,
  handleIndicatorSave,
}) => {
  const [programEnd, setProgramEnd] = useState<
    IndicatorDetails['happensAfterProgramYear']
  >(indicatorDetails.happensAfterProgramYear);

  useEffect(() => {
    setProgramEnd(indicatorDetails.happensAfterProgramYear);
  }, [indicatorDetails]);

  const handleNext = useCallback(
    (newProgramEnd?: IndicatorDetails['happensAfterProgramYear']) => {
      const value = newProgramEnd === undefined ? programEnd : newProgramEnd;
      onNext({ happensAfterProgramYear: value });
      setIndicators(s => {
        const newState = { ...s };
        newState[selectedSectionKey][
          selectedIndex
        ].happensAfterProgramYear = value;
        return newState;
      });
      const newIndicatorDetails = { ...indicatorDetails };
      newIndicatorDetails.happensAfterProgramYear = value;
      handleIndicatorSave(newIndicatorDetails);
    },
    [
      programEnd,
      selectedIndex,
      indicatorDetails,
      selectedSectionKey,
      onNext,
      setIndicators,
      handleIndicatorSave,
    ],
  );

  const handleClick = (
    newProgramEnd: IndicatorDetails['happensAfterProgramYear'],
  ) => {
    setProgramEnd(newProgramEnd);
  };

  const madLibIndicator = getMadLibForIndicator(indicator);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-page" className="program-heading">
        Will any {madLibIndicator?.unit || beneficiaryTerm}{' '}
        {madLibIndicator?.forecastOutcome || 'achieve goals'} after the end of
        this year’s program?
      </TrueImpactText>
      <div className="yes-no-buttons">
        <button
          onClick={() => handleClick('Yes')}
          className={`yes-button${programEnd === 'Yes' ? ' selected' : ''}`}
        >
          <FontAwesomeIcon icon={faThumbsUp} size="2x" className="icon" />
          Yes
        </button>
        <button
          onClick={() => handleClick('No')}
          className={`no-button${programEnd === 'No' ? ' selected' : ''}`}
        >
          <FontAwesomeIcon icon={faThumbsDown} size="2x" className="icon" />
          No
        </button>
      </div>
      <IndicatorBottomButtons
        requestState={requestState}
        disabledNext={!programEnd}
        onNext={() => handleNext()}
        onPrevious={onPrevious}
      />
    </StyledDiv>
  );
};

export default IndicatorProgramEnd;
